/**
 * Sales coaching practice: the scene, its turn events, the report and
 * moment replays. Mirrors the backend's `models/src/coaching/practice-*`
 * wire types.
 */

import {
  CoachingAttemptStatus,
  CoachingCaseFormat,
  CoachingChannel,
  CoachingCustomerGender,
  CoachingDifficulty,
  CoachingDrillResult,
  CoachingEnding,
  CoachingOpeningSide,
  CoachingRelationshipStage,
  CoachingSuggestion,
} from './coaching';
import { CoachingMilestoneView } from './coaching-progress';

export enum CoachingAttemptEndReason {
  Concluded = 'concluded',
  TurnLimit = 'turn_limit',
  Stopped = 'stopped',
  Left = 'left',
}

export enum CoachingAnchor {
  Strong = 'strong',
  Partial = 'partial',
  Poor = 'poor',
}

/** The customer's mood, low to high. */
export enum CoachingCustomerMood {
  Impatient = 'impatient',
  Guarded = 'guarded',
  Hesitant = 'hesitant',
  Open = 'open',
  Reassured = 'reassured',
}

export const kCoachingMoodOrder = [
  CoachingCustomerMood.Impatient,
  CoachingCustomerMood.Guarded,
  CoachingCustomerMood.Hesitant,
  CoachingCustomerMood.Open,
  CoachingCustomerMood.Reassured,
];

export enum CoachingCustomerAgeBand {
  Young = 'young',
  Middle = 'middle',
  Senior = 'senior',
}

export interface CoachingCustomerVoice {
  gender: CoachingCustomerGender;
  ageBand: CoachingCustomerAgeBand;
}

export enum CoachingPracticeSpeaker {
  Customer = 'customer',
  Trainee = 'trainee',
}

export interface CoachingPracticeLine {
  /** The trainee turn it belongs to; the customer's opening is turn 0. */
  turn: number;
  speaker: CoachingPracticeSpeaker;
  text: string;
}

export interface CoachingLiveArea {
  area: string;
  name: string;
  score: number;
}

export interface CoachingLiveAreaChange extends CoachingLiveArea {
  delta: number;
}

export interface CoachingObservedCriterion {
  criterionRef: string;
  name: string;
  area: string;
  anchor: CoachingAnchor;
}

export interface CoachingPracticeEnd {
  status: CoachingAttemptStatus;
  endReason: CoachingAttemptEndReason;
  ending: CoachingEnding | null;
  drillResult: CoachingDrillResult | null;
}

export interface CoachingPracticeCustomer {
  /** A display name such as 张先生. */
  name: string;
  relationshipStage: CoachingRelationshipStage;
  voice: CoachingCustomerVoice;
}

/** One spoken sentence of the customer's; `audio` is base64 MP3 or null. */
export interface CoachingPracticeAudio {
  turn: number;
  seq: number;
  text: string;
  mime: 'audio/mpeg';
  audio: string | null;
  /** How long the sentence plays; null with no audio. */
  durationMs: number | null;
  /**
   * The sentence's loudness, evenly spaced 0 to 1 levels; null with no
   * audio.
   */
  levels: number[] | null;
}

export interface CoachingPracticeView {
  attemptId: string;
  status: CoachingAttemptStatus;
  /** Exam: bare dialogue, no mood, live scores, reactions or hints. */
  exam: boolean;
  format: CoachingCaseFormat;
  difficulty: CoachingDifficulty;
  title: string;
  channel: CoachingChannel;
  customer: CoachingPracticeCustomer;
  opening: { side: CoachingOpeningSide; task: string | null };
  drillGoal: string | null;
  turns: { expected: number; max: number; used: number };
  transcript: CoachingPracticeLine[];
  mood: CoachingCustomerMood | null;
  areas: CoachingLiveArea[];
  end: CoachingPracticeEnd | null;
}

export interface CoachingPracticeOpened extends CoachingPracticeView {
  openingAudio: CoachingPracticeAudio[] | null;
}

export enum CoachingReactionKind {
  CriticalError = 'critical_error',
  Turnaround = 'turnaround',
  Strong = 'strong',
  Slip = 'slip',
}

export interface CoachingReaction {
  turn: number;
  kind: CoachingReactionKind;
  text: string;
  area: string | null;
}

export interface CoachingPracticeHint {
  turn: number;
  hint: string | null;
}

/** The events of one trainee line, as the scene receives them. */
export interface CoachingPracticeTurnCallbacks {
  onTurn?: (event: { turn: number; lastTurn: boolean }) => void;
  onCustomerText?: (event: { turn: number; text: string }) => void;
  onCustomerAudio?: (event: CoachingPracticeAudio) => void;
  onCustomerDone?: (event: {
    turn: number;
    say: string;
    lastTurnNext: boolean;
  }) => void;
  onMood?: (event: { mood: CoachingCustomerMood; shift: number }) => void;
  onEvidence?: (event: {
    turn: number;
    areas: CoachingLiveAreaChange[];
    observed: CoachingObservedCriterion[];
    criticalError: string | null;
  }) => void;
  onReaction?: (event: CoachingReaction) => void;
  onEnded?: (event: CoachingPracticeEnd) => void;
  onVerdict?: (event: { verdict: CoachingAnchor }) => void;
  onDone?: (event: { turn: number }) => void;
  /** `code` names a known failure (rate limited, cut off, line pending). */
  onError?: (message: string, code?: string) => void;
}

export interface CoachingReportCriterion {
  criterionRef: string;
  name: string;
  area: string;
  anchor: CoachingAnchor;
  turn: number | null;
  citedLine: string | null;
  hinted: boolean;
}

export interface CoachingReportMomentView {
  turn: number;
  customerLine: string;
  traineeLine: string;
  why: string;
  betterLine: string;
}

export interface CoachingReportNoteView {
  turn: number;
  text: string;
  line: string;
}

export interface CoachingReportCriticalErrorView {
  criticalError: string;
  turn: number | null;
  quote: string | null;
  why: string;
  instead: string;
}

export interface CoachingPracticeReportView {
  attemptId: string;
  status: CoachingAttemptStatus;
  exam: boolean;
  format: CoachingCaseFormat;
  difficulty: CoachingDifficulty;
  title: string;
  channel: CoachingChannel;
  customer: CoachingPracticeCustomer;
  variationLabel: string;
  end: CoachingPracticeEnd;
  outcome: string | null;
  stars: number | null;
  previousBest: number | null;
  criticalErrors: CoachingReportCriticalErrorView[];
  areas: CoachingLiveArea[];
  previousAreas: CoachingLiveArea[] | null;
  criteria: CoachingReportCriterion[];
  moments: CoachingReportMomentView[];
  strengths: CoachingReportNoteView[];
  missedOpportunities: CoachingReportNoteView[];
  drillBetterLine: string | null;
  levelUp: CoachingDifficulty | null;
  nextPractice: CoachingSuggestion | null;
  /** What this attempt's assessment earned. */
  milestones: CoachingMilestoneView[];
  transcript: CoachingPracticeLine[];
  /**
   * The lines' stored voices, matched to `transcript` by turn and speaker:
   * every line the trainee spoke, and each customer line once voiced.
   */
  voices: CoachingPracticeLineVoice[];
}

/** A line's stored voice, as the transcript lists it. */
export interface CoachingPracticeLineVoice {
  /** Plays it, as `CoachingPracticeVoiceAudio` by this id. */
  id: string;
  turn: number;
  speaker: CoachingPracticeSpeaker;
  durationMs: number;
  /** One 0 to 1 level every 50 ms. */
  levels: number[];
}

/** A stored voice's audio, to play it back. */
export interface CoachingPracticeVoiceAudio {
  id: string;
  mime: 'audio/mpeg';
  /** Base64. */
  audio: string;
  durationMs: number;
  levels: number[];
}

/** A trainee line as sent: with its parked voice when spoken. */
export interface CoachingPracticeTurnRequest {
  line: string;
  /** The trainee saw a hint before this line. */
  afterHint: boolean;
  /** Voices the customer's reply. */
  speak: boolean;
  /** The recognition's token for a spoken line; null for a typed one. */
  voiceToken: string | null;
}

export interface CoachingReplayOpened {
  replayId: string;
  attemptId: string;
  momentTurn: number;
  /** Empty when the moment is the trainee's opening line. */
  customerLine: string;
  betterLine: string;
  maxLines: number;
  title: string;
  channel: CoachingChannel;
  customer: CoachingPracticeCustomer;
  mood: CoachingCustomerMood;
  transcript: CoachingPracticeLine[];
  customerAudio: CoachingPracticeAudio[] | null;
}

export interface CoachingReplaySummary {
  replayId: string;
  momentTurn: number;
  ended: boolean;
  verdict: CoachingAnchor | null;
}

/** 再练一次 / 升一级. */
export interface CoachingAttemptAgainRequest {
  requestKey: string;
  levelUp: boolean;
}
